import { useEffect, useState } from 'react';
import { STORAGE_KEYS } from '../lib/constants';

interface SetupChecklistProps {
  applicationsCount: number;
  hasNotice: boolean;
  consentsCount: number;
  onNavigate: (view: string) => void;
}

export function SetupChecklist({
  applicationsCount,
  hasNotice,
  consentsCount,
  onNavigate,
}: SetupChecklistProps) {
  const [copiedKey, setCopiedKey] = useState(
    () => localStorage.getItem(STORAGE_KEYS.SETUP_COPIED_KEY) === 'true',
  );

  useEffect(() => {
    const handleStorageChange = () => {
      setCopiedKey(localStorage.getItem(STORAGE_KEYS.SETUP_COPIED_KEY) === 'true');
    };
    window.addEventListener('storage', handleStorageChange);
    // Also poll slightly since local updates to same window won't trigger 'storage' event
    const interval = setInterval(handleStorageChange, 1000);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const steps = [
    {
      id: 'org',
      label: 'Create Workspace Organisation',
      description: 'Your workspace has been created.',
      isCompleted: true,
      actionLabel: 'Completed',
    },
    {
      id: 'app',
      label: 'Create First Application',
      description: 'Register the software or website you want to track consents for.',
      isCompleted: applicationsCount > 0,
      actionLabel: 'Create Application',
      onClick: () => onNavigate('applications'),
    },
    {
      id: 'apikey',
      label: 'Copy Application API Key',
      description: 'Obtain the credential needed to authenticate your widget or API calls.',
      isCompleted: copiedKey,
      actionLabel: 'View API Keys',
      onClick: () => onNavigate('api-keys'),
    },
    {
      id: 'notice',
      label: 'Publish Consent Notice',
      description: 'Publish your privacy notice versions with multilingual legal purposes.',
      isCompleted: hasNotice,
      actionLabel: 'Manage Notices',
      onClick: () => onNavigate('notice-manager'),
    },
    {
      id: 'widget',
      label: 'Install Consent Widget',
      description:
        'Embed the consent widget in your website to display notices and gather consent.',
      isCompleted: consentsCount > 0,
      actionLabel: 'View Widget Details',
      onClick: () => onNavigate('overview'),
    },
    {
      id: 'test',
      label: 'Test User Consent Flow',
      description: 'Register at least one consent status to verify the integration.',
      isCompleted: consentsCount > 0,
      actionLabel: 'View Consent Logs',
      onClick: () => onNavigate('consent-log'),
    },
  ];

  const completedCount = steps.filter((s) => s.isCompleted).length;
  const totalCount = steps.length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);

  return (
    <div className="rounded-2xl border border-slate-900 bg-slate-900/10 p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">Getting Started Checklist</h2>
          <p className="mt-1 text-sm text-slate-400">
            Follow these steps to integrate DPDP consent compliance into your product.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-32 bg-slate-950 rounded-full h-2 border border-slate-800 overflow-hidden">
            <div
              className="bg-blue-500 h-full rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
          <span className="text-xs font-bold text-slate-300">
            {completedCount}/{totalCount} Completed ({progressPercent}%)
          </span>
        </div>
      </div>

      {progressPercent === 100 && (
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4 text-sm text-emerald-400 flex items-center gap-3">
          <span className="text-xl">🎉</span>
          <div>
            <p className="font-bold">Integration Fully Complete!</p>
            <p className="text-xs text-emerald-500/90 mt-0.5">
              You are officially ready for DPDP compliance. User consents are being collected and
              verified.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {steps.map((step, idx) => (
          <div
            key={step.id}
            className={`flex flex-col justify-between rounded-xl border p-5 transition ${
              step.isCompleted
                ? 'border-slate-900/60 bg-slate-950/30'
                : 'border-slate-900 bg-slate-950/70 hover:border-slate-800'
            }`}
          >
            <div>
              <div className="flex items-center gap-2">
                <div
                  className={`flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${
                    step.isCompleted
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-slate-900 text-slate-500 border border-slate-800'
                  }`}
                >
                  {step.isCompleted ? '✓' : idx + 1}
                </div>
                <h3
                  className={`text-sm font-semibold ${
                    step.isCompleted
                      ? 'text-slate-300 line-through decoration-slate-700'
                      : 'text-white'
                  }`}
                >
                  {step.label}
                </h3>
              </div>
              <p className="mt-2 text-xs text-slate-500 leading-relaxed">{step.description}</p>
            </div>

            {step.onClick && !step.isCompleted && (
              <button
                type="button"
                onClick={step.onClick}
                className="mt-4 self-start text-[11px] font-bold text-blue-400 hover:text-blue-300 transition"
              >
                {step.actionLabel} →
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
